import React, { useRef, useState, useEffect } from 'react';
import { X } from 'lucide-react';

const SwipeableModal = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  maxWidth = 'max-w-2xl',
  showCloseButton = true,
  closeOnBackdropClick = true,
  loading = false
}) => {
  const modalRef = useRef(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [translateY, setTranslateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Minimum swipe distance (in px) to trigger close
  const minSwipeDistance = 100;

  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e?.targetTouches?.[0]?.clientY);
    setIsDragging(true);
  };

  const onTouchMove = (e) => {
    if (!touchStart) return;
    
    const currentTouch = e?.targetTouches?.[0]?.clientY;
    const diff = currentTouch - touchStart;
    
    // Only allow downward swipe
    if (diff > 0) {
      setTranslateY(diff);
      setTouchEnd(currentTouch);
    }
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) {
      setIsDragging(false);
      setTranslateY(0);
      return;
    }

    const distance = touchEnd - touchStart;
    const isDownSwipe = distance > 0;

    if (isDownSwipe && distance > minSwipeDistance) {
      // Trigger close
      onClose();
    }

    // Reset
    setTouchStart(null);
    setTouchEnd(null);
    setTranslateY(0);
    setIsDragging(false);
  };

  const handleBackdropClick = (e) => {
    if (closeOnBackdropClick && e?.target === e?.currentTarget && !loading) {
      onClose();
    }
  };

  const handleCloseClick = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div 
        ref={modalRef}
        className={`bg-white rounded-lg shadow-xl ${maxWidth} w-full max-h-[90vh] overflow-y-auto transition-transform duration-200 ease-out`}
        style={{
          transform: isDragging ? `translateY(${translateY}px)` : 'translateY(0)',
          opacity: isDragging ? Math.max(0.5, 1 - translateY / 300) : 1
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Swipe Indicator */}
        <div className="flex justify-center pt-2 pb-1 md:hidden">
          <div className="w-12 h-1 bg-gray-300 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
          {showCloseButton && (
            <button
              onClick={handleCloseClick}
              className="text-gray-400 hover:text-gray-600 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation active:scale-95"
              disabled={loading}
              aria-label="Close modal"
            >
              <X className="w-6 h-6" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {children}
        </div>
      </div>
    </div>
  );
};

export default SwipeableModal;